import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";
import type {
	EventScheduleTab,
	EventScheduleType
} from "~produce/production/schedule/events/helpers/eventScheduleGrid";

import isFilledString from "~/helpers/isFilledString";
import isRecord from "~/helpers/isRecord";
import { isoWeekKey } from "~produce/dashboard/productionCalendar/helpers/calendarWeeks";

export const EVENT_WEEK_TARGET_KIND = "eventWeek";

export const EVENT_SCHEDULE_DOCK_PANEL_ID = "event-schedule";

export const EVENT_SCHEDULE_PATH = "/produce/production/schedule/events";

export interface EventWeekScope {
	eventType: EventScheduleType;
	isoWeekNumber: number;
	isoYear: number;
	siteLocationId: string;
	siteName: string;
}

const TAB_BY_EVENT_TYPE: Record<EventScheduleType, EventScheduleTab> = {
	MATERIAL: "material",
	SPACE: "space",
	TASK: "task"
};

export const eventScheduleTabForType = (eventType: EventScheduleType): EventScheduleTab => TAB_BY_EVENT_TYPE[eventType];

export const buildEventWeekTarget = ({
	eventType,
	isoWeekNumber,
	isoYear,
	siteLocationId,
	siteName
}: EventWeekScope): ContinuationTarget => ({
	targetId: JSON.stringify({ eventType, isoWeekNumber, isoYear, siteLocationId, siteName }),
	targetKind: EVENT_WEEK_TARGET_KIND
});

const parseTargetId = (targetId: string): unknown => {
	try {
		return JSON.parse(targetId);
	} catch {
		return null;
	}
};

const EVENT_TYPES: readonly unknown[] = Object.keys(TAB_BY_EVENT_TYPE);

export const isEventScheduleType = (value: unknown): value is EventScheduleType => EVENT_TYPES.includes(value);

const isIsoWeekNumber = (value: unknown): value is number =>
	typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 53;

const isIsoYear = (value: unknown): value is number =>
	typeof value === "number" && Number.isInteger(value) && value >= 1000 && value <= 9999;

export const readEventWeekScope = (target: ContinuationTarget | undefined): EventWeekScope | null => {
	if (target?.targetKind !== EVENT_WEEK_TARGET_KIND) return null;
	const scope = parseTargetId(target.targetId);
	if (!isRecord(scope)) return null;
	const { eventType, isoWeekNumber, isoYear, siteLocationId, siteName } = scope;
	if (!isEventScheduleType(eventType) || !isIsoWeekNumber(isoWeekNumber) || !isIsoYear(isoYear)) return null;
	if (!isFilledString(siteLocationId) || !isFilledString(siteName)) return null;
	return { eventType, isoWeekNumber, isoYear, siteLocationId, siteName };
};

export const withEventWeekScope = (filterModel: unknown, scope: EventWeekScope | null): unknown =>
	scope
		? {
				...((filterModel as Record<string, unknown> | null) ?? {}),
				scheduledWeek: { filter: isoWeekKey(scope), filterType: "text", type: "equals" },
				siteLocationId: { filterType: "set", values: [scope.siteLocationId] }
			}
		: filterModel;

const eventWeekReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => (readEventWeekScope(target) ? EVENT_SCHEDULE_PATH : ""),
	matchesRoute: () => null
};

export default eventWeekReceiverIdentityAdapter;
